import { getStoredTokens, setStoredTokens } from "./token-storage.js";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3000/api/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API request failed with status ${status}`);
    this.name = "ApiError";
  }
}

async function refreshAccessToken(): Promise<string | null> {
  const stored = getStoredTokens();
  if (!stored) return null;

  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: stored.refreshToken }),
  });

  if (!response.ok) {
    setStoredTokens(null);
    return null;
  }

  const tokens = (await response.json()) as { accessToken: string; refreshToken: string };
  setStoredTokens(tokens);
  return tokens.accessToken;
}

/** Cliente HTTP com anexação automática do access token e retry único após refresh em 401. */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
  allowRefresh = true,
): Promise<T> {
  const stored = getStoredTokens();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(stored ? { Authorization: `Bearer ${stored.accessToken}` } : {}),
      ...options.headers,
    },
  });

  if (response.status === 401 && stored && allowRefresh) {
    const newAccessToken = await refreshAccessToken();
    if (newAccessToken) {
      return apiFetch<T>(path, options, false);
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}
